import { useState, Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDashed,
  Clock,
  Loader2,
  Send,
  UserCog,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminPayroll,
  useAdminPayrollBreakdown,
  useAdminTeams,
  useCreatePayout,
} from '@/hooks/useAdmin';
import { useTeamScope } from '@/hooks/useTeamScope';
import { teamSection } from '@/lib/teams';
import { useUrlState } from '@/hooks/useUrlState';
import { useInvalidateMoney } from '@/hooks/useInvalidateMoney';
import { formatMoney } from '@/lib/money';
import { formatDate, formatWeekRange } from '@/lib/dates';
import {
  GLOSSARY,
  inviteStatusLabel,
  payModelLabel,
  payoutStatusLabel,
  providerLabel,
  teamLabel,
} from '@/lib/labels';
import { blockedReason, outstandingSummary, payrollRowState, type PayrollRowState } from '@/lib/payroll';
import { missingFieldLabel, parsePayrollState, rowsForTeam } from '@/lib/moneyPages';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pill, toneFor } from '@/components/ui/pill';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ActiveFilters } from '@/components/ui/active-filters';
import { channelLabel } from '@/components/ui/tag';
import {
  DataTable,
  DataTableExpandedCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { ScreenshotViewer } from '@/components/ScreenshotViewer';
import type {
  LockedValueComponentKind,
  PayrollBreakdownInvite,
  PayrollLineItem,
  PayrollPeriod,
} from '@/api/types';

/** Start-of-Monday for a given Date, as YYYY-MM-DD. */
function isoWeekStart(d: Date): string {
  const start = new Date(d);
  const day = start.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setUTCDate(start.getUTCDate() + diff);
  return start.toISOString().slice(0, 10);
}

/** First-of-month for a given Date, as YYYY-MM-DD. */
function isoMonthStart(d: Date): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

/** Where a payout goes, in words. 'unknown' means no route could be worked out. */
function routeLabel(providerKey: string): string {
  return providerKey === 'unknown' ? 'No payout route' : providerLabel(providerKey);
}

const STATE_FILTER_LABELS: Record<PayrollRowState, string> = {
  ready: 'Ready to pay',
  blocked: 'Blocked',
  in_flight: 'In flight',
  idle: 'Nothing owed',
};

export function AdminPayroll(): JSX.Element {
  const [period, setPeriod] = useState<PayrollPeriod>('weekly');
  const [periodOf, setPeriodOf] = useState(isoWeekStart(new Date()));
  const [expandedPublicId, setExpandedPublicId] = useState<string | null>(null);
  // Rows with a payout being sent right now: only those rows wait.
  const [sending, setSending] = useState<ReadonlySet<string>>(() => new Set());
  const [f, set] = useUrlState({ state: '' });
  const stateFilter = parsePayrollState(f.state);
  const { team, locked, excludeTeams } = useTeamScope();
  const lockedSection = locked ? teamSection(team) : undefined;
  const teams = useAdminTeams();
  const payroll = useAdminPayroll(periodOf, period);
  const createPayout = useCreatePayout();
  const invalidateMoney = useInvalidateMoney();

  // Team scope is applied here: the payroll endpoint returns every team.
  // With no team picked, Influencer and Vyral (own sections) are left out.
  const rows = useMemo(
    () => rowsForTeam(payroll.data?.inviters ?? [], team, excludeTeams),
    [payroll.data, team, excludeTeams],
  );
  const { owed: outstanding, credit, creditCount } = outstandingSummary(rows);

  const teamName = (code: string): string =>
    code === 'UNASSIGNED' ? 'No team' : teamLabel(code, teams.data?.find((t) => t.code === code)?.displayName);

  // Operator-facing rollup of the current payroll state — the source for the
  // summary band beneath the hero. Each tile maps to a discrete action: pay
  // now, fix details, wait for an open payout to settle, or record-keeping.
  const summary = useMemo(() => {
    let ready = 0,
      readyCount = 0;
    let blocked = 0,
      blockedCount = 0;
    let inFlight = 0,
      inFlightCount = 0;
    let idleCount = 0;
    let paid = 0,
      paidCount = 0;
    for (const inv of rows) {
      switch (payrollRowState(inv)) {
        case 'ready':
          ready += parseFloat(inv.totalDue || '0');
          readyCount += 1;
          break;
        case 'blocked':
          blocked += parseFloat(inv.totalDue || '0');
          blockedCount += 1;
          break;
        case 'in_flight':
          inFlight += parseFloat(inv.inFlight || '0');
          inFlightCount += 1;
          break;
        case 'idle':
          idleCount += 1;
          break;
      }
      const paidVal = parseFloat(inv.paidThisPeriod || '0');
      if (paidVal > 0) {
        paid += paidVal;
        paidCount += 1;
      }
    }
    return { ready, readyCount, blocked, blockedCount, inFlight, inFlightCount, idleCount, paid, paidCount };
  }, [rows]);

  const visibleRows = useMemo(
    () => (stateFilter ? rows.filter((r) => payrollRowState(r) === stateFilter) : rows),
    [rows, stateFilter],
  );

  // Group outreachers by team for visual scanning. Teams alphabetically,
  // outreachers within each team by name.
  const groupedByTeam = useMemo(() => {
    const buckets = new Map<string, PayrollLineItem[]>();
    for (const inv of visibleRows) {
      const key = inv.team || 'UNASSIGNED';
      const bucket = buckets.get(key) ?? [];
      bucket.push(inv);
      buckets.set(key, bucket);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([teamCode, items]) => ({
        team: teamCode,
        items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
        // Same rule as the hero: a paid-ahead balance doesn't offset others.
        totalDue: outstandingSummary(items).owed,
      }));
  }, [visibleRows]);

  function switchPeriod(next: PayrollPeriod): void {
    setPeriod(next);
    setPeriodOf(next === 'monthly' ? isoMonthStart(new Date()) : isoWeekStart(new Date()));
  }

  function setToCurrent(): void {
    setPeriodOf(period === 'monthly' ? isoMonthStart(new Date()) : isoWeekStart(new Date()));
  }

  function toggleState(next: PayrollRowState): void {
    set({ state: stateFilter === next ? '' : next });
  }

  async function sendPayout(
    inv: PayrollLineItem,
    extras: { payAmount?: string; settleRemainder?: boolean } = {},
  ): Promise<void> {
    const key = inv.publicId;
    setSending((prev) => new Set(prev).add(key));
    try {
      // The payout groups payments by week internally (see
      // payoutOrchestrator.startOfWeek); for monthly, the backend buckets the
      // anchor date into the week that contains it.
      const payout = await createPayout.mutateAsync({
        inviterPublicId: inv.publicId,
        body: { weekOf: periodOf, ...extras },
      });
      invalidateMoney();
      const settled = parseFloat(payout.settledAmount || '0');
      toast.success(
        `Payout for ${inv.name}: $${formatMoney(payout.totalAmount)} · ${payoutStatusLabel(payout.status)} via ${providerLabel(payout.providerKey)}${settled > 0 ? ` · $${formatMoney(payout.settledAmount)} will be written off when it's paid` : ''}`,
      );
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  const periodWord = period === 'monthly' ? 'month' : 'week';
  const payrollWeekCopy =
    period === 'weekly'
      ? `payroll week ${formatWeekRange(periodOf)}`
      : `the payroll week that contains ${formatDate(`${periodOf}T00:00:00`)}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll"
        description={
          lockedSection
            ? `What every active ${lockedSection.label} outreacher is owed. Send a payout to pay them through their payout provider.`
            : 'What every active outreacher is owed. Send a payout to pay them through their payout provider.'
        }
        actions={
          /* The biggest number on the page: everything still owed, before
             the table loads. */
          <div className="flex flex-col items-start gap-0.5 md:items-end">
            <span className="text-[0.625rem] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Outstanding (all periods)
            </span>
            <span className="text-4xl font-semibold leading-none tracking-tight tabular-nums">
              ${formatMoney(outstanding)}
            </span>
            <span className="max-w-[18rem] text-[11px] text-muted-foreground md:text-right">
              {GLOSSARY.outstanding}
            </span>
            {creditCount > 0 && (
              <span className="max-w-[18rem] text-[11px] text-muted-foreground md:text-right">
                Not counted: ${formatMoney(credit)} paid ahead to {creditCount}{' '}
                {creditCount === 1 ? 'outreacher' : 'outreachers'} (negative balance).
              </span>
            )}
          </div>
        }
      />

      {/* Four tiles answer the first payday questions: how much can I move
          right now, how much is stuck on something to fix, how much is
          already on its way, and how much has gone out this period. The first
          three filter the table (and the URL, so Home can link to them). */}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile
          tone="success"
          icon={CircleCheck}
          label="Ready to pay"
          amount={summary.ready}
          count={summary.readyCount}
          countLabel={summary.readyCount === 1 ? 'outreacher' : 'outreachers'}
          active={stateFilter === 'ready'}
          onClick={() => toggleState('ready')}
        />
        <SummaryTile
          tone="destructive"
          icon={AlertTriangle}
          label="Blocked"
          amount={summary.blocked}
          count={summary.blockedCount}
          countLabel={`need${summary.blockedCount === 1 ? 's' : ''} a fix`}
          hint={
            summary.blockedCount > 0
              ? 'Missing payment details or no payout route. Open the outreacher to fix it.'
              : undefined
          }
          active={stateFilter === 'blocked'}
          onClick={() => toggleState('blocked')}
        />
        <SummaryTile
          tone="info"
          icon={Clock}
          label="In flight"
          amount={summary.inFlight}
          count={summary.inFlightCount}
          countLabel="on the way"
          hint={
            summary.inFlightCount > 0
              ? 'A payout is queued or sent. It has to be paid or cancelled on Payouts before another can go out.'
              : undefined
          }
          active={stateFilter === 'in_flight'}
          onClick={() => toggleState('in_flight')}
        />
        <SummaryTile
          tone="muted"
          icon={Wallet}
          label={`Paid this ${periodWord}`}
          amount={summary.paid}
          count={summary.paidCount}
          countLabel={summary.paidCount === 1 ? 'outreacher' : 'outreachers'}
          hint={`Changes with the period picker below.`}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Paid this period</span>
          <div className="inline-flex rounded-md border bg-muted/30 p-0.5" role="group" aria-label="Period">
            <button
              type="button"
              aria-pressed={period === 'weekly'}
              onClick={() => switchPeriod('weekly')}
              className={`rounded px-3 py-1.5 text-xs ${period === 'weekly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Weekly
            </button>
            <button
              type="button"
              aria-pressed={period === 'monthly'}
              onClick={() => switchPeriod('monthly')}
              className={`rounded px-3 py-1.5 text-xs ${period === 'monthly' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Monthly
            </button>
          </div>
        </div>
        <div className="grid gap-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {period === 'monthly' ? 'Month of (1st)' : 'Week of (Mon)'}
          </span>
          <Input
            type="date"
            value={periodOf}
            onChange={(e) => setPeriodOf(e.target.value)}
            className="w-full md:w-48"
          />
        </div>
        <Button variant="outline" onClick={setToCurrent}>
          This {periodWord}
        </Button>
        <p className="basis-full text-xs text-muted-foreground md:basis-auto md:pb-2">
          The period only changes "Paid this {periodWord}". What's owed always covers every period.
          {period === 'weekly' && ` Showing ${formatWeekRange(periodOf)}.`}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {payroll.isLoading
            ? 'Loading'
            : `${visibleRows.length} active outreacher${visibleRows.length === 1 ? '' : 's'} across ${groupedByTeam.length} team${groupedByTeam.length === 1 ? '' : 's'}`}
        </p>
        {!stateFilter && summary.idleCount > 0 && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => toggleState('idle')}
          >
            Show the {summary.idleCount} with nothing owed
          </button>
        )}
        <ActiveFilters
          filters={
            stateFilter
              ? [{ key: 'state', label: `State: ${STATE_FILTER_LABELS[stateFilter]}`, clear: () => set({ state: '' }) }]
              : []
          }
        />
      </div>

      {payroll.isLoading ? (
        <TableSkeleton cols={['w-6', 'w-40', 'w-20', 'w-20', 'w-16', 'w-16']} />
      ) : payroll.error ? (
        <p className="text-sm text-destructive">{(payroll.error as Error).message}</p>
      ) : !payroll.data ? null : visibleRows.length === 0 ? (
        <EmptyState
          icon={<Wallet className="h-6 w-6" />}
          title={stateFilter ? `Nobody here is in "${STATE_FILTER_LABELS[stateFilter]}".` : 'No active outreachers.'}
          hint={stateFilter ? 'Clear the filter to see everyone.' : 'Outreachers show up here once they are active.'}
        />
      ) : (
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell narrow className="hidden sm:table-cell" />
              <DataTableHeaderCell>Outreacher</DataTableHeaderCell>
              <DataTableHeaderCell>State</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Outstanding</DataTableHeaderCell>
              <DataTableHeaderCell numeric>Paid this {periodWord}</DataTableHeaderCell>
              <DataTableHeaderCell />
            </tr>
          </DataTableHeader>
          <tbody>
            {groupedByTeam.map((group) => (
              <Fragment key={group.team}>
                <tr className="border-t border-border/60 bg-muted/15">
                  <td colSpan={6} className="py-1.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground/75">
                        {teamName(group.team)}
                        <span className="ml-2 font-normal text-muted-foreground normal-case tracking-normal">
                          {group.items.length}{' '}
                          {group.items.length === 1 ? 'outreacher' : 'outreachers'}
                        </span>
                      </span>
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        subtotal{' '}
                        <span className="font-semibold text-foreground/85">
                          ${formatMoney(group.totalDue)}
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>
                {group.items.map((inv) => {
                  const state = payrollRowState(inv);
                  const canPay = state === 'ready';
                  const reason = blockedReason(inv);
                  const isSending = sending.has(inv.publicId);
                  const disabledReason =
                    state === 'in_flight'
                      ? `$${formatMoney(inv.inFlight)} is already on an open payout. Finish or cancel it on Payouts first.`
                      : state === 'idle'
                        ? 'Nothing is owed right now.'
                        : undefined;
                  const isExpanded = expandedPublicId === inv.publicId;
                  const profile = `/admin/outreachers/${inv.publicId}`;
                  return (
                    <Fragment key={inv.inviterId}>
                      <DataTableRow state={isExpanded ? 'expanded' : 'default'}>
                        <td className="hidden w-6 py-2 align-middle sm:table-cell">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedPublicId((cur) =>
                                cur === inv.publicId ? null : inv.publicId,
                              )
                            }
                            className="-ml-1 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label={isExpanded ? 'Collapse breakdown' : 'Show breakdown'}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                            )}
                          </button>
                        </td>
                        <td className="py-2 font-medium">
                          <div className="flex flex-col gap-0.5">
                            <Link to={profile} className="hover:underline">
                              {inv.name}
                            </Link>
                            <span className="text-[10px] font-normal text-muted-foreground">
                              via {routeLabel(inv.lint.providerKey)}
                              {' · '}
                              {payModelLabel(inv.compensationModel)}
                            </span>
                            {state === 'blocked' && reason && (
                              <span className="whitespace-normal text-[11px] font-normal text-destructive-strong">
                                {reason}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2" data-label="State">
                          <StatePill state={state} inv={inv} />
                        </td>
                        <td
                          className={cn(
                            'py-2 text-right font-semibold tabular-nums',
                            canPay && 'text-foreground',
                            !canPay && 'text-muted-foreground',
                          )}
                          data-label="Outstanding"
                        >
                          ${formatMoney(inv.totalDue)}
                        </td>
                        <td
                          className="py-2 text-right tabular-nums text-muted-foreground"
                          data-label={`Paid this ${periodWord}`}
                        >
                          ${formatMoney(inv.paidThisPeriod)}
                        </td>
                        <td className="actions py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                          {canPay ? (
                            <PayDialog
                              inv={inv}
                              periodOf={periodOf}
                              period={period}
                              payrollWeekCopy={payrollWeekCopy}
                              pending={isSending}
                              onConfirm={(extras) => sendPayout(inv, extras)}
                            />
                          ) : state === 'in_flight' ? (
                            <Button asChild size="sm" variant="outline">
                              <Link
                                to={`/admin/payouts?inviter=${inv.inviterId}`}
                                title={disabledReason}
                              >
                                <Clock className="h-3 w-3" /> Open payout
                              </Link>
                            </Button>
                          ) : state === 'blocked' ? (
                            <Button asChild size="sm" variant="outline">
                              <Link to={profile} title={reason ?? undefined}>
                                <UserCog className="h-3 w-3" /> Fix details
                              </Link>
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled
                              title={disabledReason}
                            >
                              <Send className="h-3 w-3" />
                              Send payout
                            </Button>
                          )}
                          </div>
                        </td>
                      </DataTableRow>
                      {isExpanded && (
                        <DataTableRow state="expanded">
                          <td className="hidden sm:table-cell"></td>
                          <DataTableExpandedCell colSpan={5}>
                            <PayrollBreakdownPanel
                              inviter={inv}
                              periodOf={periodOf}
                              period={period}
                            />
                          </DataTableExpandedCell>
                        </DataTableRow>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}

interface SummaryTileProps {
  tone: 'success' | 'destructive' | 'info' | 'muted';
  icon: typeof CircleCheck;
  label: string;
  amount: number;
  count: number;
  countLabel: string;
  hint?: string;
  /** When set, the tile is a filter toggle for the table. */
  onClick?: () => void;
  active?: boolean;
}

function SummaryTile({
  tone,
  icon: Icon,
  label,
  amount,
  count,
  countLabel,
  hint,
  onClick,
  active = false,
}: SummaryTileProps): JSX.Element {
  const toneClass = {
    success: 'border-success/35 bg-success/[0.04] text-success-strong',
    destructive: 'border-destructive/30 bg-destructive/[0.04] text-destructive-strong',
    info: 'border-info/30 bg-info/[0.04] text-info-strong',
    muted: 'border-border bg-muted/30 text-muted-foreground',
  }[tone];
  const isZero = amount === 0 && count === 0;
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]">
        <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
        {label}
        {active && <span className="ml-auto normal-case tracking-normal">Filtering</span>}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-xl font-semibold tabular-nums text-foreground">
          ${formatMoney(amount)}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {count} {countLabel}
        </span>
      </div>
      {hint && !isZero && (
        <div className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-muted-foreground">
          {hint}
        </div>
      )}
    </>
  );
  const className = cn(
    'flex flex-col gap-1 rounded-md border p-3 text-left transition-colors',
    toneClass,
    isZero && !active && 'opacity-70',
    active && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
  );
  if (!onClick) {
    return (
      <div className={className} title={hint}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={cn(className, 'hover:bg-accent/40')}
      title={hint}
      aria-pressed={active}
      onClick={onClick}
    >
      {body}
    </button>
  );
}

function StatePill({ state, inv }: { state: PayrollRowState; inv: PayrollLineItem }): JSX.Element {
  switch (state) {
    case 'ready':
      return (
        <Pill tone="paid" title={`$${formatMoney(inv.totalDue)} ready to send via ${routeLabel(inv.lint.providerKey)}.`}>
          Ready
        </Pill>
      );
    case 'blocked': {
      const missing = inv.lint.missingFields;
      const label =
        inv.lint.providerKey === 'unknown'
          ? 'No payout route'
          : missing.length === 1
            ? `Missing ${missingFieldLabel(missing[0]!.field)}`
            : `Missing ${missing.length} details`;
      const detail = missing.map((m) => missingFieldLabel(m.field)).join(', ');
      return (
        <Pill
          tone="rejected"
          icon={AlertTriangle}
          title={[blockedReason(inv), detail ? `Missing: ${detail}.` : ''].filter(Boolean).join(' ')}
        >
          {label}
        </Pill>
      );
    }
    case 'in_flight':
      return (
        <Pill tone="submitted" title={`$${formatMoney(inv.inFlight)} is on a payout that's queued or sent.`}>
          In flight ${formatMoney(inv.inFlight)}
        </Pill>
      );
    case 'idle':
      return (
        <Pill tone="muted" icon={CircleDashed}>
          Nothing owed
        </Pill>
      );
  }
}

// ── Pay dialog ──────────────────────────────────────────────────────────

/** Decimal string → integer cents, preview-grade (the backend re-derives). */
function toCents(v: string | null | undefined): number {
  const n = parseFloat(v ?? '0');
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/**
 * Confirm-and-send for one outreacher's payout. Pays the full amount owed by
 * default; a smaller custom amount turns on the settlement path — the unpaid
 * remainder is written off per invite (greedy, oldest invite first, same
 * rule as the backend's allocateSettlement) and never returns to payroll.
 * Retainer outreachers have no custom amount: the backend rejects payAmount
 * for them, and their payouts are recorded internally without a provider.
 */
function PayDialog({
  inv,
  periodOf,
  period,
  payrollWeekCopy,
  pending,
  onConfirm,
}: {
  inv: PayrollLineItem;
  periodOf: string;
  period: PayrollPeriod;
  payrollWeekCopy: string;
  pending: boolean;
  onConfirm: (extras: { payAmount?: string; settleRemainder?: boolean }) => Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [settleChecked, setSettleChecked] = useState(false);

  const isRetainer = inv.compensationModel === 'RETAINER';
  const owedCents = toCents(inv.totalDue);

  // Per-invite owed amounts for the write-off preview. Fetched only once the
  // dialog opens; shares the breakdown panel's query cache.
  const breakdown = useAdminPayrollBreakdown(
    open && !isRetainer ? inv.publicId : null,
    periodOf,
    period,
  );

  const trimmed = amountInput.trim();
  const amountValid = /^\d+(\.\d{1,2})?$/.test(trimmed);
  const payCents = trimmed === '' ? owedCents : amountValid ? toCents(trimmed) : null;
  const isPartial = payCents !== null && payCents > 0 && payCents < owedCents;
  const amountError =
    trimmed === ''
      ? null
      : payCents === null || payCents <= 0
        ? 'Enter a dollar amount above 0, like 125.00.'
        : payCents > owedCents
          ? `That's more than the $${formatMoney(inv.totalDue)} owed. To pay extra, adjust an invite first.`
          : null;

  // Mirror of the backend's allocateSettlement: invites in id order, each
  // paid min(owed, remaining); whatever can't be covered is written off.
  const allocation = useMemo(() => {
    if (payCents === null || payCents <= 0 || payCents >= owedCents || !breakdown.data) {
      return null;
    }
    const invites = breakdown.data.byStatus
      .flatMap((g) => g.invites)
      .filter((i) => toCents(i.contribution) > 0)
      .sort((a, b) => a.inviteId - b.inviteId);
    let remaining = payCents;
    return invites.map((i) => {
      const owed = toCents(i.contribution);
      const paid = Math.min(owed, remaining);
      remaining -= paid;
      return {
        inviteId: i.inviteId,
        targetIdentifier: i.targetIdentifier,
        paid,
        writeOff: owed - paid,
      };
    });
  }, [payCents, owedCents, breakdown.data]);

  const sendCents = payCents !== null && payCents > 0 ? Math.min(payCents, owedCents) : owedCents;
  const writeOffCents = isPartial ? owedCents - sendCents : 0;
  const writeOffInviteCount = allocation?.filter((a) => a.writeOff > 0).length ?? null;
  const canConfirm = !pending && amountError === null && (!isPartial || settleChecked);

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) {
      setAmountInput('');
      setSettleChecked(false);
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!canConfirm) return;
    await onConfirm(isPartial ? { payAmount: trimmed, settleRemainder: true } : {});
    handleOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="default" disabled={pending}>
          {pending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Send className="h-3 w-3" />
          )}
          Send payout
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void handleConfirm();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{`Send ${inv.name} $${formatMoney(sendCents / 100)}?`}</DialogTitle>
          <DialogDescription asChild>
            <span className="space-y-1 text-sm">
              <span className="block">
                Sends ${formatMoney(sendCents / 100)} through {routeLabel(inv.lint.providerKey)}
                {inv.paypalEmail ? (
                  <>
                    {' '}to <span className="font-medium text-foreground">{inv.paypalEmail}</span>
                  </>
                ) : null}
                {isPartial
                  ? ` — less than the $${formatMoney(inv.totalDue)} owed. It is recorded for ${payrollWeekCopy}.`
                  : `. It covers everything owed so far and is recorded for ${payrollWeekCopy}.`}
              </span>
              <span className="block">
                You can cancel it on Payouts until it's paid. Once the provider pays it, it
                can't be undone here.
              </span>
            </span>
          </DialogDescription>
        </DialogHeader>

        {!isRetainer && (
          <div className="grid gap-1.5">
            <Label htmlFor={`pay-amount-${inv.publicId}`}>Amount to send</Label>
            <Input
              id={`pay-amount-${inv.publicId}`}
              inputMode="decimal"
              placeholder={formatMoney(inv.totalDue)}
              value={amountInput}
              onChange={(e) => {
                setAmountInput(e.target.value);
                setSettleChecked(false);
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Leave empty to pay the full ${formatMoney(inv.totalDue)}. Enter less to pay
              that amount and settle the rest.
            </p>
            {amountError && <p className="text-xs text-destructive">{amountError}</p>}
          </div>
        )}

        {isPartial && (
          <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/[0.04] p-3">
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={settleChecked}
                onChange={(e) => setSettleChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
              />
              <span>
                Settle the remainder — write off{' '}
                <span className="font-semibold tabular-nums">
                  ${formatMoney(writeOffCents / 100)}
                </span>
                {writeOffInviteCount !== null
                  ? ` across ${writeOffInviteCount} invite${writeOffInviteCount === 1 ? '' : 's'}`
                  : ''}
                . This never returns to payroll. New earnings on these invites still count
                later, and nothing is written off if the payout fails or is cancelled.
              </span>
            </label>
            {breakdown.isLoading ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Working out the per-invite split
              </p>
            ) : allocation && allocation.length > 0 ? (
              <ul className="max-h-40 space-y-0.5 overflow-y-auto border-t pt-2 text-xs">
                {allocation.map((a) => (
                  <li key={a.inviteId} className="flex items-baseline justify-between gap-2">
                    <span className="truncate">{a.targetIdentifier}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      pay ${formatMoney(a.paid / 100)}
                      {a.writeOff > 0 && (
                        <span className="text-destructive-strong">
                          {' '}· settle ${formatMoney(a.writeOff / 100)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canConfirm} onClick={() => void handleConfirm()}>
            {pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {isPartial
              ? `Send $${formatMoney(sendCents / 100)} · settle $${formatMoney(writeOffCents / 100)}`
              : `Send $${formatMoney(inv.totalDue)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Breakdown panel ─────────────────────────────────────────────────────

function PayrollBreakdownPanel({
  inviter,
  periodOf,
  period,
}: {
  inviter: PayrollLineItem;
  periodOf: string;
  period: PayrollPeriod;
}): JSX.Element {
  const query = useAdminPayrollBreakdown(inviter.publicId, periodOf, period);

  if (query.isLoading) {
    return (
      <div className="flex items-center px-4 py-4 text-xs text-muted-foreground">
        <Loader2 className="mr-2 h-3 w-3 animate-spin" /> Loading breakdown
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <p className="px-4 py-4 text-xs text-destructive">
        {(query.error as Error)?.message ?? "Couldn't load the breakdown"}
      </p>
    );
  }
  const data = query.data;
  const isRetainer = data.inviter.compensationModel === 'RETAINER';
  const contributingStatuses = data.byStatus.filter((s) => parseFloat(s.contribution) > 0);
  const nonContributing = data.byStatus.filter((s) => parseFloat(s.contribution) <= 0);

  return (
    <div className="grid gap-4 px-4 py-4 lg:grid-cols-[1.6fr_1fr]">
      <div className="space-y-3">
        {/* Pay snapshot — moved off the main row so the table stays scannable.
            The four values behind the Outstanding number: owed before
            adjustments, adjustments, retainer, and where the money goes. */}
        <div className="grid gap-2 rounded-md border border-border bg-card p-3 sm:grid-cols-4">
          <SnapshotField label="Owed (before adjustments)" value={`$${formatMoney(inviter.owed)}`} />
          <SnapshotField
            label="Adjustments"
            value={`$${formatMoney(inviter.adjustmentsTotal)}`}
            emphasis={parseFloat(inviter.adjustmentsTotal || '0') !== 0}
          />
          <SnapshotField
            label="Weekly retainer"
            value={`$${formatMoney(inviter.retainerDue)}`}
            hint={inviter.retainerAmount ? `$${formatMoney(inviter.retainerAmount)} a week` : undefined}
          />
          <SnapshotField
            label="PayPal email"
            value={inviter.paypalEmail ?? '—'}
            mono
            emphasis={!inviter.paypalEmail}
          />
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            Where ${formatMoney(data.totalDue)} comes from
          </span>
          <span className="text-[10px] text-muted-foreground">
            {payModelLabel(data.inviter.compensationModel)}
          </span>
        </div>
        {isRetainer ? (
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            Weekly retainer: <span className="tabular-nums">${formatMoney(data.retainerDue)}</span> for
            this {period === 'monthly' ? 'month' : 'week'}. Rate card amounts for logged, joined
            and posted invites aren't added for outreachers on a weekly retainer.
          </div>
        ) : contributingStatuses.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Nothing is owed right now.
          </p>
        ) : (
          contributingStatuses.map((group) => (
            <details
              key={group.status}
              open={group.status === 'CONVERTED' || contributingStatuses.length === 1}
              className="rounded-md border bg-muted/20"
            >
              <summary className="flex cursor-pointer items-baseline justify-between gap-2 px-3 py-2 text-sm">
                <span className="flex items-center gap-2">
                  <Pill tone={toneFor(group.status)}>{inviteStatusLabel(group.status)}</Pill>
                  <span className="text-muted-foreground">
                    {group.count} {group.count === 1 ? 'invite' : 'invites'}
                  </span>
                </span>
                <span className="font-semibold tabular-nums">
                  ${formatMoney(group.contribution)}
                </span>
              </summary>
              <ul className="divide-y border-t text-xs">
                {group.invites
                  .filter((inv) => parseFloat(inv.contribution) > 0)
                  .map((inv) => (
                    <li key={inv.inviteId} className="px-3 py-2">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <span className="truncate font-medium">{inv.targetIdentifier}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {channelLabel(inv.channel)}
                            {inv.followerCount !== null
                              ? ` · ${inv.followerCount.toLocaleString()} followers`
                              : ''}
                            {inv.convertedAt
                              ? ` · posted ${formatDate(inv.convertedAt)}`
                              : ` · logged ${formatDate(inv.createdAt)}`}
                            {parseFloat(inv.adjustmentsTotal) !== 0
                              ? ` · adjusted ${parseFloat(inv.adjustmentsTotal) > 0 ? '+' : '-'}$${formatMoney(Math.abs(parseFloat(inv.adjustmentsTotal)))}`
                              : ''}
                          </span>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <ScreenshotViewer
                            scope="admin"
                            publicId={inv.publicId}
                            targetIdentifier={inv.targetIdentifier}
                            hasScreenshot={!!inv.screenshotKey}
                            size="sm"
                          />
                          <span className="tabular-nums">
                            ${formatMoney(inv.contribution)}
                          </span>
                        </div>
                      </div>
                      <LockedValueBreakdownRow invite={inv} />
                    </li>
                  ))}
              </ul>
            </details>
          ))
        )}
        {nonContributing.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            <span className="font-medium">Not contributing: </span>
            {nonContributing
              .map((g) => `${g.count} ${inviteStatusLabel(g.status).toLowerCase()}`)
              .join(' · ')}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Recent payouts</span>
            <Link to={`/admin/payouts?inviter=${inviter.inviterId}`} className="text-xs text-primary hover:underline">
              All payouts
            </Link>
          </div>
          {data.recentPayouts.length === 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">No payouts yet.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-xs">
              {data.recentPayouts.slice(0, 5).map((p) => (
                <li
                  key={p.publicId}
                  className="flex items-baseline justify-between gap-2 rounded-sm bg-muted/20 px-2 py-1"
                >
                  <span className="flex items-center gap-2">
                    <Pill tone={toneFor(p.status)}>{payoutStatusLabel(p.status)}</Pill>
                    <span className="text-muted-foreground">
                      {p.paidAt ? `Paid ${formatDate(p.paidAt)}` : formatWeekRange(p.payrollWeekOf)}
                    </span>
                  </span>
                  <span className="tabular-nums">${formatMoney(p.totalAmount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-md border bg-muted/20 p-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Paid this {period === 'monthly' ? 'month' : 'week'}</span>
            <span className="tabular-nums">${formatMoney(data.paidThisPeriod)}</span>
          </div>
          {isRetainer && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Retainer due</span>
              <span className="tabular-nums">${formatMoney(data.retainerDue)}</span>
            </div>
          )}
          <div className="flex justify-between border-t pt-1 font-semibold">
            <span>Outstanding</span>
            <span className="tabular-nums">${formatMoney(data.totalDue)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SnapshotField({
  label,
  value,
  hint,
  mono = false,
  emphasis = false,
}: {
  label: string;
  value: string;
  hint?: string;
  mono?: boolean;
  emphasis?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'truncate text-sm tabular-nums text-foreground/90',
          mono && 'font-mono text-[12px]',
          emphasis && 'font-semibold text-foreground',
        )}
        title={value}
      >
        {value}
      </span>
      {hint && (
        <span className="text-[10px] text-muted-foreground">{hint}</span>
      )}
    </div>
  );
}

// Short-label overrides for the fine-grained component kinds. Fall through
// to the server-provided `label` for anything not listed here (including
// future kinds this frontend doesn't recognize yet).
const COMPONENT_KIND_LABEL: Partial<Record<LockedValueComponentKind, string>> = {
  BASE_LOG: 'Logged',
  NICHE_LOG: 'Logged · niche bonus',
  BASE_JOIN: 'Joined',
  NICHE_JOIN: 'Joined · niche bonus',
  BASE_POST: 'Posted',
  NICHE_POST: 'Posted · niche bonus',
  BRACKET_BONUS: 'Follower bonus',
  FLOOR_AGAINST_PAID: 'Kept at amount already paid',
};

function LockedValueBreakdownRow({
  invite,
}: {
  invite: PayrollBreakdownInvite;
}): JSX.Element | null {
  const components = invite.lockedValueComponents;
  if (!components || components.length === 0) return null;
  return (
    <details className="mt-1.5 text-[10px] text-muted-foreground">
      <summary className="cursor-pointer select-none">
        How it was earned ({components.length})
      </summary>
      <ul className="mt-1 space-y-0.5 pl-3">
        {components.map((c, i) => (
          <li
            key={`${c.kind}-${i}`}
            className="flex items-baseline justify-between gap-2"
          >
            <span>{COMPONENT_KIND_LABEL[c.kind] ?? c.label}</span>
            <span className="tabular-nums">${formatMoney(c.amount)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
